/**
 * PORT-008 §8.1 — la coque d'un espace. **Frère** d'`AppLayout`, jamais enfant.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CE QU'ELLE NE MONTE PAS, ET POURQUOI CHAQUE ABSENCE COMPTE
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ `EntityProvider`, `PageEntityProvider` — un compte d'espace n'a pas
 *    d'entité courante : il a un CONTEXTE, qui est un rattachement.
 * ⛔ `useTenantConfig` — `GET /api/tenant/config` rend **403** à un `PORTAL`
 *    (PORT-002 §4). L'appeler ici ferait clignoter une erreur à chaque montage.
 * ⛔ `/auth/me` — même raison : 403 `PORTAL_ACCOUNT_ONLY`. La seule sonde
 *    d'identité d'un espace est `/api/portal/me`.
 *
 * L'ordre des gardes n'est pas décoratif :
 *   1. `/me` en chargement → squelette (jamais une redirection : on ne sait
 *      pas encore s'il y a une session, et rediriger ferait sortir quelqu'un
 *      de connecté à chaque rafraîchissement) ;
 *   2. pas de session (401, `SANS_SESSION`) → `/espace` (**jamais** `/login`) ;
 *   3. **panne** (tout autre échec) → l'erreur, DANS la marque, avec
 *      « Réessayer ». 🔴 La garde 2 s'écrivait `!data`, qui couvre AUSSI
 *      l'échec : un 5xx renvoyait vers la connexion, la connexion renvoyait
 *      ici, et la boucle ne disait rien (FE-13) ;
 *   4. aucun contexte actif → la coque ENTRE SEULE s'il n'y a rien à choisir
 *      (un seul espace, ou un défaut mémorisé encore servi) ; sinon
 *      `/espace/choisir`. Voir `useEntreeAutomatique`.
 */

import { useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { SANS_SESSION, codeDeLErreur, usePortalMe } from '@/hooks/usePortalMe'
import { useIsMobile } from '@/hooks/use-mobile'
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_MOBILE } from '@/components/ui/sidebar'

import { PortalBrandTheme } from './PortalBrandTheme'
import { PortalErreurPleinePage } from './PortalErreurPleinePage'
import { PortalHeader } from './PortalHeader'
import { PortalSidebar } from './PortalSidebar'
import { useChoisirLEspace, useSeDeconnecterDeLEspace } from './useChoisirLEspace'
import { useEntreeAutomatique } from './useEntreeAutomatique'

function Squelette() {
    return (
        <div className="flex min-h-dvh flex-col gap-4 p-6">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-64 w-full" />
        </div>
    )
}

export function PortalLayout() {
    const { t } = useTranslation()
    const [menuOuvert, setMenuOuvert] = useState(false)
    const { data, isPending, error, refetch } = usePortalMe()
    // Un seul chemin pour entrer dans un espace et en sortir, partagé avec la
    // page de choix : ses deux copies échouaient en silence (FE-8).
    const choisirLeContexte = useChoisirLEspace()
    const seDeconnecter = useSeDeconnecterDeLEspace()
    // Appelé AVANT les gardes : un crochet ne se pose jamais sous un `return`.
    // ⚠️ Rien n'est tenté pendant une ERREUR : React Query garde l'ancienne réponse
    // quand une relecture échoue, et la coque croirait l'entrée encore à faire.
    const entreeAutomatiqueEnCours = useEntreeAutomatique(error ? undefined : data)

    /**
     * ⭐ **Un seul décideur de palier dans toute la solution : `useIsMobile()`.**
     *
     * `CPORT-016 §5.2` et `18_CPORT/_INTRO` l'écrivent (« trois paliers et un
     * seul décideur »), et la MESURE le confirme : 55 fichiers basculent par ce
     * hook, dont `DataTable.tsx:424` (`cardMode = isMobile === true`) — c'est
     * lui qui fait passer toutes les tables du produit en tuiles. La coque
     * portait un SECOND seuil écrit en Tailwind (`lg:`, 1024 px), d'où une bande
     * morte de 768 à 1023 px et tout son responsive décalé de 256 px.
     *
     * ⛔ Ne pas revenir à une classe `md:` : elle ne sait pas exprimer le second
     * cas du hook — le téléphone en PAYSAGE (`max-width:1023px` ET
     * `max-height:499px`), soit précisément l'intervenant qui tourne son
     * téléphone entre deux salles. Une classe utilitaire y rendrait la barre
     * latérale sur 390 px de haut.
     *
     * `=== true` et non la vérité JS : le hook rend `undefined` sans `matchMedia`
     * (tests, SSR), et un `undefined` traité comme « mobile » masquerait la barre
     * latérale sur un poste de bureau.
     */
    const estMobile = useIsMobile() === true

    if (isPending) return <Squelette />
    if (data === SANS_SESSION) return <Navigate to="/espace" replace />
    if (!data) {
        return (
            <PortalErreurPleinePage
                code={codeDeLErreur(error)}
                onReessayer={() => void refetch()}
                onSeDeconnecter={seDeconnecter}
            />
        )
    }

    // PORT-005 §250 — aucun contexte actif. 🔴 Ce commentaire affirmait qu'un
    // espace unique « est sélectionné par le serveur à l'entrée » : rien ne le
    // faisait, et une personne à un seul espace tombait sur une page de choix à
    // une ligne. C'est la COQUE qui entre, pendant qu'elle montre son squelette ;
    // la page de choix ne sert que s'il y a vraiment à choisir, ou si l'entrée
    // automatique a été refusée. Zéro contexte servi : l'accueil dit pourquoi.
    // A12 — un espace fermé PENDANT la visite (`active_context_closed`) n'ouvre rien
    // seul : la page de choix dit « fermé » (`useEntreeAutomatique`). ⛔ La seconde
    // moitié de cette condition reste : à zéro espace, AUCUNE des deux surfaces ne
    // navigue (`PortalAucunEspace`).
    if (!data.active_context_id && data.contexts.length > 0) {
        // 🔴 Revue adversariale du 2026-09-15 : une entrée ACCEPTÉE suivie d'une
        // relecture de `/me` en échec (panne, ou fiche éteinte refusée par la couche
        // b) laissait la coque sur son squelette POUR TOUJOURS — `data` restait
        // l'ancienne réponse, sans contexte actif. L'échec se dit, avec « Réessayer »
        // et une sortie.
        if (error) {
            return (
                <PortalErreurPleinePage
                    code={codeDeLErreur(error)}
                    onReessayer={() => void refetch()}
                    onSeDeconnecter={seDeconnecter}
                />
            )
        }
        if (entreeAutomatiqueEnCours) return <Squelette />
        return <Navigate to="/espace/choisir" replace />
    }

    const contexteActif =
        data.contexts.find((c) => c.id === data.active_context_id) ?? null

    return (
        <div className="min-h-dvh bg-background">
            {/*
                ⭐ **Le lien d'évitement** — `PORT-008 §5.3` : « premier élément
                focalisable, visible au focus, cible le `<main>` : il APPARTIENT
                au shell, jamais à un écran ». La spec notait elle-même qu'il
                n'existait **nulle part** dans le dépôt (`grep` → 0).

                Sans lui, une personne au clavier retraverse le menu entier à
                chaque page (WCAG 2.4.1) — et le menu d'un espace n'est pas court :
                socle plus entrées de facette. Aucun des 22 constats de la revue
                « navigation » ne l'avait vu ; c'est la passe adversariale qui l'a
                trouvé, en relisant la spec au lieu du code.

                `sr-only focus:not-sr-only` : invisible à la souris, présent au
                clavier. ⛔ Ne pas le remplacer par un `hidden` — un élément
                `display:none` n'est pas focalisable, et le lien serait mort.
            */}
            <a
                href="#portal-contenu"
                className="sr-only rounded-md bg-background px-4 py-2 text-sm font-medium underline focus:not-sr-only focus:absolute focus:left-4 focus:top-[calc(0.5rem+env(safe-area-inset-top))] focus:z-50 focus:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                {t('portal.nav.skipToContent')}
            </a>

            <PortalBrandTheme
                name={data.organisation.name}
                primaryColor={data.organisation.primary_color}
            />

            <div className="flex min-h-dvh">
                {/* Deux corrections en une, toutes deux nommées par `PORT-008 §5.3`.

                    ① La largeur vient de `SIDEBAR_WIDTH` : « Largeur desktop =
                    `SIDEBAR_WIDTH` = `16rem` (`ui/sidebar.tsx`), **jamais une
                    valeur en dur** ». Le `w-64` d'avant valait la même chose par
                    coïncidence — deux sources pour un nombre, qui divergent au
                    premier ajustement de l'une des deux.

                    ② Rendu CONDITIONNEL, jamais `hidden lg:block` : une barre
                    montée puis masquée reste dans le DOM, et `PortalSidebar` s'y
                    trouvait monté DEUX fois au téléphone — une fois dans l'aside
                    invisible, une fois dans le volet. Deux instances du même
                    menu, et un `getByRole('navigation')` qui en trouve deux. */}
                {!estMobile && (
                    <aside className="shrink-0 border-r" style={{ width: SIDEBAR_WIDTH }}>
                        <PortalSidebar
                            organisation={data.organisation}
                            contexteActif={contexteActif}
                            onDeconnexion={seDeconnecter}
                        />
                    </aside>
                )}

                <Sheet open={menuOuvert} onOpenChange={setMenuOuvert}>
                    <SheetContent side="left" className="p-0" style={{ width: SIDEBAR_WIDTH_MOBILE }}>
                        <SheetTitle className="sr-only">{t('portal.nav.label')}</SheetTitle>
                        <PortalSidebar
                            organisation={data.organisation}
                            contexteActif={contexteActif}
                            onNavigate={() => setMenuOuvert(false)}
                            onDeconnexion={seDeconnecter}
                        />
                    </SheetContent>
                </Sheet>

                <div className="flex min-w-0 flex-1 flex-col">
                    <PortalHeader
                        user={data.user}
                        contextes={data.contexts}
                        contexteActif={contexteActif}
                        onOuvrirLeMenu={() => setMenuOuvert(true)}
                        onChoisirLeContexte={choisirLeContexte}
                        onDeconnexion={seDeconnecter}
                    />
                    {/* Zones sûres LATÉRALES — `PORT-008 §8.6` les écrit mot pour
                        mot : « `main` `p-4 md:p-6` avec
                        `pl/pr-[max(1rem,env(safe-area-inset-*))]` ». Elles
                        manquaient : `index.html:5` porte `viewport-fit=cover`,
                        donc en PAYSAGE sur un appareil à encoche le document
                        couvre le bloc capteurs — 44 à 59 px contre 16 px de
                        gouttière — et le début de chaque ligne passe dessous.

                        `max()` à l'horizontale, `calc()` en bas : l'inset latéral
                        vaut DÉJÀ 44-59 px et s'ajouter à la gouttière ferait payer
                        deux fois (AppLayout.tsx:155-158) ; les 34 px de la barre
                        d'accueil, eux, ne sont pas une gouttière de lecture.

                        ⚠️ §8.6 prescrit aussi `pb-[calc(5.5rem+…)]` « pour ne pas
                        passer sous l'aide flottante » — la coque d'espace n'en
                        monte AUCUNE (vérifié : zéro occurrence sous
                        `components/portal`). Recopier la réserve donnerait 88 px
                        de vide en bas de chaque écran. À reprendre le jour où
                        l'aide flottante entre dans l'espace. */}
                    <main id="portal-contenu" className="min-w-0 flex-1 p-4 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pb-[calc(1.5rem+env(safe-area-inset-bottom))] md:p-6 md:pl-[max(1.5rem,env(safe-area-inset-left))] md:pr-[max(1.5rem,env(safe-area-inset-right))]">
                        <ErrorBoundary>
                            <Outlet />
                        </ErrorBoundary>
                    </main>
                </div>
            </div>
        </div>
    )
}
